<template>
    <v-app v-if="!getWaitingForThemeOverride" :class="`${$vuetify.breakpoint.smAndDown ? 'mobile-app': ''}`" id="app">
        <v-layout column>
            <v-main>
                <div
                    v-if="popUpActivated || cookiePopUpDisplayed"
                    aria-hidden="false"
                    tabindex="0"
                    class="visually-hidden"
                    :aria-label="$t('accessibility.popup.closed')"
                    id="prepend-skip-to" />
                <VueSkipTo v-if="getRouteName !== 'take-appt'" to="" :label="$t('header.accessibilitySkipToContentLink')" id="skip-to" />
                <VueSkipTo v-else to="" :label="$t('header.accessibilityGoToCurrentStep')" id="skip-to" />
                <title-bar />
                <div :class="`app-wrapper ${$route.path.includes('coupon') ? 'coupon' : ''} tz-content-wrapper pb-0`">
                    <establishment-header />
                    <template v-if="isPublicPageBlocked">
                        <shedding-page />
                    </template>
                    <template v-else>
                        <tz-loading :value="isHomeLoading" />
                        <v-flex>
                            <transition name="fade" mode="out-in">
                                <router-view v-show="!isHomeLoading" />
                            </transition>
                        </v-flex>
                    </template>
                </div>
            </v-main>
            <app-footer />
            <trimoz-snackbar />
            <trimoz-error-dialog />

            <!-- Displays a Cookie Consent form -->
            <cookie-consent
                tabindex="0"
                aria-hidden="false"
                cookie-name="privacyConsent"
                cookie-path="/"
                :cookie-domain="tld"
                :cookie-expiry-days="365"
                id="cookie-consent">
                <template
                    slot="message"
                >
                    <span tabindex="0" aria-hidden="false" class="cookie-consent-message">
                        {{ cookieConsentMessage }}
                        <a
                            role="button"
                            :href="consentLink"
                            target="_blank"
                            rel="noopener"
                            class="cookie-consent-link">
                            {{ cookieConsentLinkLabel }}
                        </a>
                    </span>
                </template>
                <template slot="button">
                    <button class="v-btn btn-info" @click="onClickCookieConsent">
                        Ok
                    </button>
                </template>
            </cookie-consent>
        </v-layout>
    </v-app>
</template>

<script>
import 'vue-recaptcha';
import { mapGetters } from 'vuex';
import { get, includes, isObject } from 'lodash';
import TitleBar from './components/TitleBar';
import TzLoading from './components/TzLoading';
import TrimozSnackbar from './components/trimozSnackBar/TrimozSnackbar';
import CookieConsent from 'vue-cookieconsent-component';
import { inferCookieDomain, inferConsentDomain } from '@/helpers/env';
import TrimozErrorDialog from './components/TrimozErrorDialog';
import SheddingPage from './components/SheddingPage';
import EstablishmentHeader from './components/establishmentHeader/EstablishmentHeader.vue';
import AppFooter from '@/components/appFooter/AppFooter';
import { setSkipToCustomClickEvent, setFocusForAccessibility } from '@/plugins/accessibility';

export default {
    name: 'app',
    components: {
        AppFooter,
        TrimozErrorDialog,
        TitleBar,
        TrimozSnackbar,
        TzLoading,
        CookieConsent,
        SheddingPage,
        EstablishmentHeader,
    },
    data() {
        return {
            recaptchaScript: null,
            cookiePopUpDisplayed: false,
        };
    },
    mounted() {
        // Otherwise it may cause an issue on page refresh (recaptcha taking other lg)
        setTimeout(() => {
            this.setRecaptchaScript();
        }, 500);

        if (includes(document.URL, 'gapFlag=1')) {
            this.$store.commit(
                'appointments/UPDATE_GAP_FLAG',
                true
            );
        }

        this.cookiePopUpDisplayed = document.querySelector('#cookie-consent') !== null;
    },
    updated() {
        if(!this.getRouteName) {
            return;
        }
        this.applyA11yFocusOutProblemFix();
    },
    watch: {
        locale(newLocale, oldLocale) {
            if (newLocale && oldLocale &&
                    newLocale !== oldLocale) {
                this.setRecaptchaScript();
            }
        },
        '$route'(to) {
            if (isObject(to.meta.title)) {
                document.title = to.meta.title[this.locale] + ' | ' + get(this.getEstablishment, 'name', '');
            }
        },
    },
    computed: {
        ...mapGetters({
            locale: 'locale',
            getWaitingForThemeOverride: 'getWaitingForThemeOverride',
            isHomeLoading: 'loading/isHomeLoading',
            isPublicPageBlocked: 'getPublicPageBlocked',
            getTexts: 'texts/getTexts',
            getEstablishment: 'establishment/getEstablishment',
        }),
        tld() {
            return inferCookieDomain();
        },
        consentLink() {
            return '//' + inferConsentDomain() + this.$t('base.cookieConsent.urlPath');
        },
        cookieConsentMessage() {
            return this.$t('base.cookieConsent.message');
        },
        cookieConsentLinkLabel() {
            return this.$t('base.cookieConsent.linkLabel');
        },
        cookieConsentButtonLabel() {
            return this.$t('base.cookieConsent.buttonLabel');
        },
        getRouteName() {
            return this.$route.name;
        },
        popUpActivated() {
            return this.getTexts[this.locale].isPopupActivated;
        }
    },
    methods: {
        setRecaptchaScript() {
            const recaptcha = document.getElementById('recaptcha');
            if (recaptcha) {
                document.body.removeChild(recaptcha);
            }

            this.recaptchaScript = document.createElement('script');
            this.recaptchaScript.id = 'recaptcha';
            this.recaptchaScript.src = 'https://www.google.com/recaptcha/api.js?onload=vueRecaptchaApiLoaded&render=explicit';
            this.recaptchaScript.src += `&hl=${this.locale}`;
            this.recaptchaScript.async = true;
            this.recaptchaScript.defer = true;

            document.body.appendChild(this.recaptchaScript);
        },
        /**
         * Using the attribut "to="#skip-to-main-content-start" of the VueSkipTo Vue component in our context provoke an unexplainable focus lost.
         * The consequence is that with Window Narrator, it describe again the home page main description (e.g.: « Home page Clic Santé ... »)
         * instead of describing the main content (e.g.: « H1 DISCOVER THE SERVICES »).
         *
         * The code below fix this issue by :
         * 1. Replacing the click event on the skip to main content element with our custom approach.
         * 2. Clearing the focus event (focus out).
         * 3. Imposing a small delay of 10ms with setTimeout.
         * 4. Focus in H1#skip-to-main-content-start.
         */
        applyA11yFocusOutProblemFix() {
            setSkipToCustomClickEvent(this.getRouteName);
        },
        onClickCookieConsent() {
            setFocusForAccessibility('prepend-skip-to');
        }
    },

};
</script>

<style lang="scss">
    @import './assets/scss/variables';

    #app {
        /*font-family: 'Avenir', Helvetica, Arial, sans-serif;*/
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        background: $whiteSmoke;

        .app-wrapper {
            padding: 6px;
            background: $whiteSmoke;
        }

        @media only screen and (min-width: 955px) {
            .app-wrapper.coupon {
                padding: 6px 0;
            }
        }

        & .v-application--wrap {
            @media only screen and (max-width: 768px) {
                margin-bottom: 80px;
            }
        }
    }

    .fade-enter-active, .fade-leave-active {
        transition: opacity .5s;
    }
    .fade-enter, .fade-leave-to {
        opacity: 0;
    }

    .g-recaptcha {
        display: inline-block;
    }

    .wysiwyg-output {
        b, strong {
            font-weight: 800;
        }
    }
    .screenreader-only:not(:focus) {
        position: absolute;
        left: -10000px;
        top: auto;
        width: 1px;
        height: 1px;
        overflow: hidden;
    }

    /* fix for accessibility : The "Continue" button loses focus when the Windows narrator is open  */
    .screenreader-only:focus {
      position: absolute;
    }

    .vue-skip-to {
        z-index: 9999 !important;
    }

    /*
    * For a11y. If not present, some focused element have a chance of being behind the bottom menu on a mobile view.
    * Note: user partialy blind that required intensive zoom (e.g.: 300% zoom) often trigger the mobile environment.
    *
    * Unfortunately, setting the height value like so break the display of the vuetify autocomplete and select component.
    * Therefore, we comment it until we have time to find a solution that won't break the scroll and satisfy accessibility demand above.
    */
    #app.mobile-app {
        /* height: calc(100vh - 80px) !important; */
        overflow-y: auto !important;

        footer .v-btn:not(.v-btn--round).v-size--default {
            height: inherit !important;
        }
    }
</style>
